import type { BacktestFormState } from "./types.ts";
import type { StrategyRuleDSL } from "./strategy-builder.ts";

export const STARTER_SAMPLE_SEEN_KEY = "backtest.web.starterSample.v1.seen";
export const STARTER_SAMPLE = {
  publicationID: "pub_V7DB-_riGm6xZL_kbdKiXgOxblFZQXQtmlf2n36tB7E",
  publishedAt: "2026-09-03T04:02:25Z", publicationExpiresAt: "2026-10-03T04:02:03Z",
  sourceResultSHA256: "ca44e525e9442745590acdfff5be186211d01b41273a4ccc92da5785853bc49d",
  symbol: "XAU/USD", timeframe: "1h", strategy: "SMA 9 / 21",
  start: "2026-06-03T03:00:00Z", end: "2026-09-03T03:00:00Z",
  proxy: "GC=F", provider: "yfinance", totalReturn: 0.63,
  netProfit: 63.32, drawdown: 1.49, tradeCount: 35, initialCapital: 10000,
} as const;

interface StarterStorage {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function browserStarterStorage(): Storage | null {
  try { return typeof window === "undefined" ? null : window.localStorage; }
  catch { return null; }
}

/** Capture before draft/history migrations run. Unknown persisted state fails closed. */
export function hasPreviousStarterState(storage: StarterStorage | null | undefined): boolean {
  if (!storage) return true;
  try {
    if (storage.getItem(STARTER_SAMPLE_SEEN_KEY) !== null) return true;
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index) || "";
      if (key.startsWith("backtest.web.draft.")
        || key.startsWith("backtest.web.localHistory")
        || key === "backtest.web.experienceMode"
        || key === "backtest.web.riskNoticeAccepted") return true;
    }
    return false;
  } catch { return true; }
}

export function hasStarterURLInput(search: string, hash: string): boolean {
  const params = new URLSearchParams(search);
  return Boolean(hash) || ["symbol", "timeframe", "period", "strategy", "direction", "mode", "reproduce", "share", "attribution"].some((key) => params.has(key));
}

export interface StarterEligibility {
  authResolved: boolean;
  accountID: string | null;
  hasPreviousState: boolean;
  explicitInput: boolean;
  hasEarlyEdits: boolean;
  firstHydration: boolean;
  entryPath: string;
}

/** A once-only local setup selection. No result, history, quota or network writes. */
export function claimStarterSample(storage: StarterStorage | null | undefined, state: StarterEligibility): boolean {
  if (!state.authResolved || !state.firstHydration || !storage) return false;
  try {
    if (storage.getItem(STARTER_SAMPLE_SEEN_KEY) !== null) return false;
    storage.setItem(STARTER_SAMPLE_SEEN_KEY, "seen");
    return !state.accountID && !state.hasPreviousState && !state.explicitInput && !state.hasEarlyEdits
      && ["/", "/backtest"].includes(state.entryPath.replace(/\/$/, "") || "/");
  } catch { return false; }
}

export function starterSampleForm(): BacktestFormState {
  const strategyDSL: StrategyRuleDSL = {
    schema_version: 2, catalog_version: "2026-07-22", short_mode: "mirror_long",
    entry: { id: "entry", match: "all", conditions: [{ id: "entry.1", left: { kind: "indicator", id: "sma", parameters: { period: 9 } }, operator: "crosses_above", right: { kind: "indicator", id: "sma", parameters: { period: 21 } } }] },
    exit: { id: "exit", match: "any", conditions: [{ id: "exit.1", left: { kind: "indicator", id: "sma", parameters: { period: 9 } }, operator: "crosses_below", right: { kind: "indicator", id: "sma", parameters: { period: 21 } } }] },
    risk: { mode: "inherit" },
  };
  const startDate = STARTER_SAMPLE.start.slice(0, 10);
  const endDate = STARTER_SAMPLE.end.slice(0, 10);
  return {
    symbol: "XAU/USD", timeframe: "1h", period: "Custom", strategyMode: "builder",
    strategyName: "SMA 9 / 21", prompt: "Visual Strategy Builder", strategyDSL,
    initialCapital: 10000, commissionRate: 0.1, slippageBps: 5, positionSizePct: 2,
    executionModel: "futures", marginMode: "isolated", leverage: 10,
    maintenanceMarginPercent: 0.5, fundingRatePercent: 0, fundingIntervalHours: 8,
    liquidationFeePercent: 0.5, stopLossPct: 2, takeProfitPct: 4,
    tradeDirection: "long", exitMode: "signal_and_risk", useAI: false,
    startDate, endDate,
    restoredDateRange: { start: STARTER_SAMPLE.start, end: STARTER_SAMPLE.end, inputStart: startDate, inputEnd: endDate },
  };
}
